import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import {
  ArrowBackIosRounded as BackIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { NavbarWrapper, useEditorStore, useIsOnline } from '@diana/core';
import { useFlowBuilder2PageContext } from '../../Context/useFlowBuilder2PageContext';
import { FlowBuilder2PageViewNameEnum } from '../../Context/FlowBuilder2PageContext.types';
import { SaveStatusIndicator } from '../../Components/SaveStatusIndicator/SaveStatusIndicator';
import { PublishButton } from '../../Components/PublishButton/PublishButton';
import { FLOW_BUILDER2_PAGE_NS } from '../../i18n';

export const PreviewViewNavbar = () => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const { palette } = useTheme();

  const isOnline = useIsOnline();

  const flowName = useEditorStore((s) => s.flow?.properties.displayName ?? '');
  const {
    isLoading: isPageLoading,
    error: pageError,
    setActiveView,
  } = useFlowBuilder2PageContext();

  const content = useMemo(() => {
    if (isPageLoading)
      return (
        <>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="rounded" width={250} height={15} />
        </>
      );
    if (!pageError)
      return (
        <>
          <ViewIcon color="primary" />
          <Typography>{t('titles.workflowPreview')}</Typography>
          <Typography>-</Typography>
          <Typography>{flowName || t('labels.untitledWorkflow')}</Typography>
        </>
      );
  }, [isPageLoading, pageError, flowName, t]);

  return (
    <NavbarWrapper hideMainNavbar sx={{ backgroundColor: palette.background.paper }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
            backgroundColor: ({ palette }) => palette.background.paperHighElevation,
          },
        }}
      >
        <Box>
          <IconButton
            sx={{ p: 0.75 }}
            onClick={() => setActiveView(FlowBuilder2PageViewNameEnum.Builder)}
          >
            <BackIcon fontSize="small" />
          </IconButton>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          {content}
        </Stack>
        {isOnline && (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SaveStatusIndicator />
            <PublishButton />
          </Stack>
        )}
      </Box>
    </NavbarWrapper>
  );
};
